'use client'
import { Icon } from '@/components/ui/icon'
import { ContentImage } from '@/components/ui/content-image'
import { useAppDispatch } from '@/lib/store'
import { ProductItem } from '@/lib/types/operations/product'
import { createOptionalEventHandler } from '@/lib/utils/event-adapters'
import { Button, cn } from '@heroui/react'
import { useRouter } from 'next/navigation'
import { useCallback, useMemo } from 'react'
import { AddToCartButton } from './add-to-cart-button'
import { Bone } from '@/components/ui/bone'

interface ProductCardProps {
  product: ProductItem
  className?: string
  isRelated?: boolean
  categoryName?: string
  onAddToCart?: (product: ProductItem) => void
}

// Card image fallback: product-level media, then an in-stock variant's
// swatch photo, then the first variant with any photo at all. Wrap-vinyl
// products (PWF, SW900, 2080) carry no product-level media, only per-variant
// swatch photography (see scripts/supplier-assets/wire-supplier-swatches.ts
// and wire-product-hero-images.ts), so without this chain every card on
// those catalogs falls straight to the placeholder.
const resolveCardImage = (product: ProductItem): { url?: string; alt: string } => {
  const alt = product.name || 'Product image'
  if (product.media?.[0]?.url) return { url: product.media[0].url, alt }

  const variants = (product.variants as Array<{ id: string; media?: Array<{ url: string }> }>) || []
  const variantsWithMedia = variants.filter((v) => v.media?.[0]?.url)
  if (variantsWithMedia.length === 0) return { url: undefined, alt }

  const inStockVariantIds = new Set(
    (product.inventoryItems || [])
      .filter((item) => item.variantId && Number(item.quantity) > 0)
      .map((item) => item.variantId)
  )
  const chosen = variantsWithMedia.find((v) => inStockVariantIds.has(v.id)) || variantsWithMedia[0]
  return { url: chosen.media?.[0]?.url, alt }
}

// Helper to check product inventory status
const getInventoryStatus = (
  product: ProductItem
): {
  hasInventory: boolean
  text: string
} => {
  // For products with variants, check variant-level inventory
  if (product.variants && product.variants.length > 0) {
    let totalVariantQuantity = 0

    // Check each variant's inventory in product's inventoryItems
    product.variants.forEach((variant) => {
      const variantInventoryItems = product.inventoryItems?.filter((item) => item.variantId === variant.id) || []

      totalVariantQuantity += variantInventoryItems.reduce((sum, item) => sum + (Number(item.quantity) || 0), 0)
    })

    // Add any product-level inventory that might exist
    const productInventory = product.inventory?.quantity || 0
    const productInventoryItems = product.inventoryItems?.filter((item) => !item.variantId) || []

    const productLevelQuantity =
      productInventory + productInventoryItems.reduce((sum, item) => sum + (Number(item.quantity) || 0), 0)

    // Total inventory is the sum of variant-level and product-level inventory
    const totalQuantity = totalVariantQuantity + productLevelQuantity

    if (totalQuantity > 10) {
      return { hasInventory: true, text: 'In Stock' }
    } else if (totalQuantity > 0) {
      return { hasInventory: true, text: 'Low Stock' }
    }
  } else {
    // For simple products, check product-level inventory
    const totalQuantity = product.inventoryItems?.reduce((sum, item) => sum + (Number(item.quantity) || 0), 0) || 0

    // Add inventory.quantity if it exists
    const inventoryQuantity = product.inventory?.quantity || 0
    const finalQuantity = totalQuantity + inventoryQuantity

    if (finalQuantity > 10) {
      return { hasInventory: true, text: 'In Stock' }
    } else if (finalQuantity > 0) {
      return { hasInventory: true, text: 'Low Stock' }
    }
  }

  return { hasInventory: false, text: 'Out of Stock' }
}

export function ProductCard({
  product,
  className,
  isRelated = false,
  categoryName,
  onAddToCart
}: ProductCardProps) {
  const router = useRouter()
  const dispatch = useAppDispatch()

  // Check if product has variants
  const hasVariants = useMemo(() => {
    return Array.isArray(product.variants) && product.variants.length > 0
  }, [product])

  const inventoryStatus = getInventoryStatus(product)
  const cardImage = useMemo(() => resolveCardImage(product), [product])

  const handleCategoryClick = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault()
      e.stopPropagation()
      router.push(`/categories/${product.categoryId}`)
    },
    [router, product.categoryId]
  )

  const handleOpenProductPage = useCallback(
    createOptionalEventHandler((e?: React.MouseEvent) => {
      // Use slug for the URL if available, otherwise fall back to ID
      router.push(`/products/${product.slug || product.id}`)
    }),
    [router, product.slug, product.id]
  )

  // Determine the display name for the category
  const displayCategoryName = useMemo(() => {
    return categoryName || product.categoryId
  }, [categoryName, product.categoryId])

  return (
    <div
      className={cn(
        'w-full group transition-transform hover:scale-[1.02]',
        className,
        hasVariants && 'border-warning/50'
      )}
      data-testid='card'
    >
      {/* Category badge in top right */}
      <div className='relative top-2 right-2 z-10'>
        <span
          className='inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-overlay text-muted cursor-pointer hover:bg-accent hover:text-accent-foreground transition-colors'
          onClick={handleCategoryClick}
        >
          {displayCategoryName}
        </span>
      </div>

      {/* If this card is rendered as a related product, show a small badge */}
      {isRelated && (
        <div className='absolute top-2 left-2 z-10'>
          <span className='inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-accent text-accent-foreground'>
            Related
          </span>
        </div>
      )}

      {/* Product badges for featured, sponsored, or promoted products */}
      <div className='absolute bottom-2 left-2 z-10 flex flex-wrap gap-1'>
        {product.isSponsored && (
          <span className='inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-warning text-warning-foreground' data-testid='chip'>
            <Icon icon='currency-usd' className='mr-1 w-3 h-3' />
            Sponsored
          </span>
        )}
        {product.isPromoted && (
          <span className='inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-accent text-accent-foreground' data-testid='chip'>
            <Icon icon='trending-up' className='mr-1 w-3 h-3' />
            Promoted
          </span>
        )}
      </div>

      {/* Product Image */}
      <div className='flex items-center justify-center overflow-hidden bg-black'>
        {product.salePrice && product.salePrice < (product.price || 0) && (
          <span className='absolute top-2 left-2 z-10 inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold bg-accent text-accent-foreground'>
            {product.salePrice && product.salePrice > 0 ? `${product.salePrice}% OFF` : `$${product.salePrice} OFF`}
          </span>
        )}
        <div
          onClick={(e) => {
            e.stopPropagation()
            handleOpenProductPage(e)
          }}
          className='cursor-pointer w-full'
        >
          <ContentImage
            src={cardImage.url}
            alt={cardImage.alt}
            contentType="PRODUCT"
            className='z-0 w-full h-auto object-cover object-center transition-transform group-hover:scale-110'
            fallbackSrc='/images/placeholder-product.jpg'
            data-testid={`image-${product.slug}`}
          />
        </div>
      </div>

      {/* Product Info */}
      <div className='px-4 pt-3 pb-0'>
        <div className='flex flex-col gap-1'>
          <h3 className='font-semibold text-medium line-clamp-1'>{product.name}</h3>
        </div>
      </div>

      <div className='px-4 py-2'>
        {product.description && <p className='text-small text-default-500 line-clamp-2'>{product.description}</p>}

        {/* Price Display */}
        <div className='mt-2 mb-1'>
          {product.salePrice && product.salePrice < (product.price || 0) ? (
            <div className='flex items-center gap-2'>
              <span className='text-danger font-bold'>${product.salePrice.toFixed(2)}</span>
              <span className='text-default-500 line-through'>${product.price?.toFixed(2)}</span>
            </div>
          ) : (
            <span className='font-semibold'>${product.price?.toFixed(2)}</span>
          )}
        </div>

        {/* Inventory Status */}
        <div className='mt-1 flex flex-row gap-2 items-center'>
          <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded text-xs font-medium ${
            inventoryStatus.hasInventory ? 'bg-success/15 text-foreground' : 'bg-danger/15 text-foreground'
          }`}>
            <span className={`inline-block w-1.5 h-1.5 rounded-full ${inventoryStatus.hasInventory ? 'bg-success' : 'bg-danger'}`} />
            {inventoryStatus.text}
          </span>
          {product.isFeatured && (
            <span className='inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-warning/15 text-foreground' data-testid='chip'>
              <Icon icon='star-bold' className='w-3 h-3' />
              Featured
            </span>
          )}
        </div>
      </div>

      <div className='px-4 pt-0 pb-3 flex flex-col gap-2'>
        <div className='flex items-center gap-2 w-full'>
          {/* View Product Details Button */}
          <Button
            size='sm'
            className='flex-1 cursor-pointer'
            onPress={handleOpenProductPage}
            title='View Details'
            data-testid='button-open-product-page'
          >
            Details
          </Button>

          {/* Add to Cart Button */}
          <AddToCartButton
            product={product}
            size='sm'
            iconOnly
            data-testid={`add-to-cart-${product.id}`}
            className='flex-1'
            showPrice={false}
          />
        </div>
      </div>
    </div>
  )
}

(ProductCard as any).Skeleton = function ProductCardSkeleton({ count = 1 }: { count?: number }) {
  return (
    <>
      {Array.from({ length: count }).map((_, i) => (
        <div
          key={i}
          style={{ animationDelay: `${i * 0.06}s` }}
          className="w-full"
        >
          {/* Product image (object-contain, no fixed aspect on real img; approximate with 4/3) */}
          <Bone className="w-full aspect-[4/3] rounded-none" />
          {/* Product name (text-medium line-clamp-1) */}
          <div className="px-4 pt-3 pb-0">
            <Bone className="h-4 w-3/4" />
          </div>
          {/* Description (text-small line-clamp-2) + price + inventory chip */}
          <div className="px-4 py-2">
            <div className="space-y-2">
              <Bone className="h-4 w-full" />
              <Bone className="h-4 w-2/3" />
            </div>
            <Bone className="h-5 w-20 mt-2 mb-1" />
            <div className="mt-1">
              <Bone className="h-5 w-24 rounded" />
            </div>
          </div>
          {/* Action buttons (size sm, flex-1) */}
          <div className="px-4 pt-0 pb-3">
            <div className="flex items-center gap-2 w-full">
              <Bone className="h-8 flex-1" />
              <Bone className="h-8 flex-1" />
            </div>
          </div>
        </div>
      ))}
    </>
  )
}
